import { gsap } from "gsap"
import { Observer } from "gsap/Observer"
import { module as BaseModule } from "modujs"

gsap.registerPlugin(Observer)

export default class extends BaseModule {
  init() {
    this.content = this.el.querySelector('[data-infinite-scroll-content]')
    this.scrollY = 0
    this.offsetY = 0
    this.baseOffset = 0
    this.itemHeight = 0
    this.scrollSpeed = 0.25
    this.dragSpeed = 1
    this.inertiaFriction = 0.92
    this.inertiaThreshold = 0.15
    this.animationId = null
    this.inertiaId = null
    this.introTween = null
    this.isReady = false

    this.onResize = this.onResize.bind(this)

    this.setupInfiniteScroll()
    this.setupObserver()
    this.startAnimation()

    window.addEventListener('resize', this.onResize)
  }

  setupInfiniteScroll() {
    if (!this.content) return

    // Duplicate frames as siblings for a seamless loop
    const frames = [...this.content.children]
    frames.forEach((frame) => {
      this.content.appendChild(frame.cloneNode(true))
    })

    this.waitForImages().then(() => {
      this.measure()
      this.playIntro()
    })
  }

  waitForImages() {
    const images = [...this.content.querySelectorAll('img')]

    return Promise.all(
      images.map((img) => {
        if (img.complete) return Promise.resolve()

        return new Promise((resolve) => {
          img.addEventListener('load', resolve, { once: true })
          img.addEventListener('error', resolve, { once: true })
        })
      })
    )
  }

  measure() {
    if (!this.content) return

    const frames = [...this.content.children]
    const originalCount = frames.length / 2
    if (!originalCount) {
      this.itemHeight = 0
      this.baseOffset = 0
      return
    }

    // Height of one full set (used for looping)
    this.itemHeight = this.content.scrollHeight / 2

    // Offset by the middle of the first frame so it bleeds from the top
    const firstFrame = frames[0]
    this.baseOffset = -(firstFrame.offsetHeight / 2)
  }

  playIntro() {
    if (!this.itemHeight) {
      gsap.set(this.el, { opacity: 1 })
      this.isReady = true
      return
    }

    this.offsetY = 0
    this.isReady = true

    this.introTween?.kill()
    this.introTween = gsap
      .timeline()
      .to(
        this.el,
        {
          opacity: 1,
          duration: 1,
          ease: "power2.out",
        },
        0
      )
      .to(
        this,
        {
          offsetY: this.baseOffset,
          duration: 1.2,
          ease: "power2.out",
        },
        0
      )
  }

  onResize() {
    this.measure()

    if (!this.isReady) return

    // Keep the settled offset in sync after resize (skip intro tween)
    if (this.introTween?.isActive()) {
      this.introTween.kill()
      gsap.set(this.el, { opacity: 1 })
    }

    this.offsetY = this.baseOffset
  }

  setupObserver() {
    this.observer = new Observer({
      target: window,
      type: "wheel,touch,pointer",
      dragMinimum: 3,
      preventDefault: true,
      allowClicks: true,
      ignore: "a, button, input, textarea, select",
      onWheel: (self) => {
        if (!this.isReady) return
        this.stopInertia()
        this.applyDelta(self.deltaY * this.scrollSpeed)
      },
      onDragStart: () => {
        if (!this.isReady) return
        this.stopInertia()
      },
      onDrag: (self) => {
        if (!this.isReady) return
        this.applyDelta(-self.deltaY * this.dragSpeed)
      },
      onDragEnd: (self) => {
        if (!this.isReady) return
        this.startInertia(-self.velocityY)
      },
    })
  }

  applyDelta(delta) {
    this.scrollY += delta
  }

  startInertia(velocity) {
    this.stopInertia()

    let v = velocity * 0.016

    if (Math.abs(v) < this.inertiaThreshold) return

    const tick = () => {
      if (Math.abs(v) < this.inertiaThreshold) {
        this.inertiaId = null
        return
      }

      this.applyDelta(v)
      v *= this.inertiaFriction
      this.inertiaId = requestAnimationFrame(tick)
    }

    this.inertiaId = requestAnimationFrame(tick)
  }

  stopInertia() {
    if (this.inertiaId) {
      cancelAnimationFrame(this.inertiaId)
      this.inertiaId = null
    }
  }

  startAnimation() {
    const animate = () => {
      if (this.itemHeight) {
        const normalizedScroll = this.scrollY % this.itemHeight
        const yPosition = normalizedScroll < 0
          ? this.itemHeight + normalizedScroll
          : normalizedScroll

        gsap.set(this.content, { y: this.offsetY - yPosition })
      }

      this.animationId = requestAnimationFrame(animate)
    }

    animate()
  }

  destroy() {
    this.stopInertia()
    window.removeEventListener('resize', this.onResize)

    if (this.introTween) {
      this.introTween.kill()
    }
    if (this.observer) {
      this.observer.kill()
    }
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
    }
  }
}
